import { Hotel, ItineraryItem, TripInfo } from '../types/Quotation';
import { getDatePlusDays } from './date';

type PlaceBoundary = {
  start: number;
  end: number;
  idx: number;
};

type HotelStayPoint = {
  key: string;
  name: string;
  checkInDate: string;
  checkOutDate: string;
};

const HOTEL_STAY_POINT_PATTERN = /^(Check-in at|Checkout from)\s+/i;

const normalizeDateOnly = (value?: string) => (
  typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : ''
);

const buildPlaceBoundaries = (places: TripInfo['places']): PlaceBoundary[] => (
  places.reduce<PlaceBoundary[]>((acc, place, idx) => {
    const start = acc.length === 0 ? 0 : acc[acc.length - 1].end;
    const end = start + Math.max(0, Number(place.nights || 0));
    acc.push({ start, end, idx });
    return acc;
  }, [])
);

const resolvePlaceIndexForDay = (dayIndex: number, boundaries: PlaceBoundary[]) => {
  const match = boundaries.find((boundary) => dayIndex >= boundary.start && dayIndex <= boundary.end);
  if (match) return match.idx;
  return Math.max(0, boundaries.length - 1);
};

const resolveHotelIdentity = (hotel: Partial<Hotel>, dayIndex: number, hotelIndex: number) => {
  const anyHotel = hotel as any;
  return String(
    anyHotel.hotelId ||
    anyHotel.hotel_id ||
    anyHotel.hotelRef ||
    anyHotel.hotel_ref ||
    anyHotel.providerHotelId ||
    anyHotel.provider_hotel_id ||
    hotel.name ||
    `${dayIndex}-${hotelIndex}`
  ).trim();
};

const collectHotelStays = (
  itinerary: ItineraryItem[],
  startDate: string,
  places: TripInfo['places']
): HotelStayPoint[] => {
  const boundaries = buildPlaceBoundaries(places);
  const seen = new Set<string>();
  const stays: HotelStayPoint[] = [];

  itinerary.forEach((day, dayIndex) => {
    (day.hotels || []).forEach((hotel, hotelIndex) => {
      const placeIdx = resolvePlaceIndexForDay(dayIndex, boundaries);
      const boundary = boundaries[placeIdx];
      const fallbackCheckInDate = boundary
        ? getDatePlusDays(startDate, boundary.start)
        : getDatePlusDays(startDate, dayIndex);
      const fallbackCheckOutDate = boundary
        ? getDatePlusDays(startDate, boundary.end)
        : '';
      const anyHotel = hotel as any;
      const checkInDate = normalizeDateOnly(anyHotel.checkInDate || anyHotel.check_in_date) || fallbackCheckInDate;
      const checkOutDate = normalizeDateOnly(anyHotel.checkOutDate || anyHotel.check_out_date) || fallbackCheckOutDate;
      const name = String(hotel.name || '').trim();
      if (!name || !checkInDate || !checkOutDate) return;

      const key = `${resolveHotelIdentity(hotel, dayIndex, hotelIndex)}|${checkInDate}|${checkOutDate}`;
      if (seen.has(key)) return;
      seen.add(key);
      stays.push({
        key,
        name,
        checkInDate,
        checkOutDate,
      });
    });
  });

  return stays;
};

export const buildHotelPointsByDay = (
  itinerary: ItineraryItem[],
  startDate: string,
  places: TripInfo['places']
): string[][] => {
  const days = Array.from({ length: itinerary.length }, () => [] as string[]);
  if (!startDate || places.length === 0) return days;

  const stays = collectHotelStays(itinerary, startDate, places);
  for (let dayIndex = 0; dayIndex < itinerary.length; dayIndex += 1) {
    const dayDate = getDatePlusDays(startDate, dayIndex);
    stays.forEach((stay) => {
      if (stay.checkInDate === dayDate) {
        days[dayIndex].push(`Check-in at ${stay.name}`);
      }
      if (stay.checkOutDate === dayDate) {
        days[dayIndex].push(`Checkout from ${stay.name}`);
      }
    });
  }

  return days;
};

export const isHotelStayPointText = (value?: string) => HOTEL_STAY_POINT_PATTERN.test(String(value || '').trim());

export const stripHotelStayPoints = (points: string[] = []) => points.filter((point) => !isHotelStayPointText(point));
